import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post } from '../api/client.js';

const KEY = ['foreman', 'crew-status'];

// Returns supervised crew (foreman view) or every active user (admin view)
// with each person's current shift + today's JHA status. One round-trip
// powers CrewRosterPanel without per-row fetches.
export function useSupervisedCrew(opts = {}) {
  return useQuery({
    queryKey: KEY,
    queryFn: async () => (await get('/api/foreman/crew-status')).crew || [],
    // Refetch every 15s so the displayed elapsed times and JHA status
    // stay fresh across browser sessions (another admin clocking the same
    // crew in elsewhere would otherwise only show on next mutation here).
    refetchInterval: 15_000,
    staleTime: 5_000,
    ...opts,
  });
}

export function useForemanClockIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => post('/api/foreman/clock/in', payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ['timesheet'] });
    },
  });
}

export function useForemanClockOut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => post('/api/foreman/clock/out', payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ['timesheet'] });
    },
  });
}

// Force-close a stuck shift for a supervised crew member. Server-side
// rule: shift must be open >= app_settings.payroll.stuck_shift_hours.
// Reason is required and lands in both the entry notes and audit row.
export function useForceCloseShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ target_user_id, reason }) =>
      post('/api/foreman/clock/force-close', { target_user_id, reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ['timesheet'] });
    },
  });
}
